import { PremiumDiscountAnalysisPeriod } from '@models';
import { PremiumDiscountDTO } from '@types';
import { MapperFactory } from './mapper-factory';
import { getFirstNonEmptyProp, parseStdNumber } from './mapper.utils';
import { Mapper, MapperParams } from './type.mapper';
import { Logger } from '@utils/logger';
import { EMDASH } from '../constants/product.constants';

const logger = Logger.getLogger('PremiumDiscountMapper');

export const sortPremiumDiscountDataPoints = (
  a: PremiumDiscountDTO,
  b: PremiumDiscountDTO
): number => parseStdNumber(a.dispsortorder) - parseStdNumber(b.dispsortorder);

export class PremiumDiscountMapper extends Mapper<PremiumDiscountAnalysisPeriod> {
  constructor(mapperParams: MapperParams, mapperFactory: MapperFactory) {
    super(mapperParams, mapperFactory);
  }

  toDomain(data: PremiumDiscountDTO[]): PremiumDiscountAnalysisPeriod {
    // if no data, return null
    if (!data || data.length === 0) {
      return null;
    }

    logger.debug('data', data);

    // initial empty period object. Data will get added later
    const period: PremiumDiscountAnalysisPeriod = {
      asOfDate: getFirstNonEmptyProp(data, 'asofdate'),
      label: null,
      value: null,
      statistics: [],
      dataPoints: [],
    };

    // map data points for chart+table
    period.dataPoints = data
      .sort(sortPremiumDiscountDataPoints)
      .map((obj: PremiumDiscountDTO) => ({
        label: obj.mktvalrangedesc,
        breakdown: obj.noofdays,
        breakdownStd: parseStdNumber(obj.noofdaysstd),
      }));

    // 6 specific stats for each period
    period.statistics = [
      {
        label: this.mapperParams.translateService.instant(
          'products.total-days-in-period'
        ),
        val: getFirstNonEmptyProp(data, 'totdaysinperiod'),
        caveatPlacement: 'TotalDaysInPeriodPriceDistribution',
      },
      {
        label: this.mapperParams.translateService.instant(
          'products.days-at-nav'
        ),
        val: getFirstNonEmptyProp(data, 'daysatnav'),
        caveatPlacement: 'DaysAtNAVPriceDistribution',
        tooltip: this.mapperParams.translateService.tooltip(
          'products.days-at-nav'
        ),
      },
      {
        label: this.mapperParams.translateService.instant(
          'products.days-at-premium'
        ),
        val: getFirstNonEmptyProp(data, 'daysatpremium'),
        caveatPlacement: 'DaysAtPremiumPriceDistribution',
        tooltip: this.mapperParams.translateService.tooltip(
          'products.days-at-premium'
        ),
      },
      {
        label: this.mapperParams.translateService.instant(
          'products.days-at-discount'
        ),
        val: getFirstNonEmptyProp(data, 'daysatdiscount'),
        caveatPlacement: 'DaysAtDiscountPriceDistribution',
        tooltip: this.mapperParams.translateService.tooltip(
          'products.days-at-discount'
        ),
      },
      {
        label: this.mapperParams.translateService.instant(
          'products.greatest-premium'
        ),
        val: getFirstNonEmptyProp(data, 'greatestpremiumlocal')
          ? getFirstNonEmptyProp(data, 'greatestpremiumlocal')
          : EMDASH,
        caveatPlacement: 'GreatestPremiumPriceDistribution',
        tooltip: this.mapperParams.translateService.tooltip(
          'products.greatest-premium'
        ),
        asOfLabel: this.mapperParams.translateService.instant('products.on'),
        asOfDate: getFirstNonEmptyProp(data, 'greatestpremiumdatelocal'),
      },
      {
        label: this.mapperParams.translateService.instant(
          'products.greatest-discount'
        ),
        val: getFirstNonEmptyProp(data, 'greatestdiscountlocal'),
        caveatPlacement: 'GreatestDiscountPriceDistribution',
        tooltip: this.mapperParams.translateService.tooltip(
          'products.greatest-discount'
        ),
        asOfLabel: this.mapperParams.translateService.instant('products.on'),
        asOfDate: getFirstNonEmptyProp(data, 'greatestdiscountdatelocal'),
      },
    ];

    return period;
  }
}
